import {equals} from 'ol/coordinate.js';
import olFeature from 'ol/Feature.js';
import {unByKey} from 'ol/Observable.js';
import olGeomPoint from 'ol/geom/Point.js';
import olStyleStyle from 'ol/style/Style.js';
import olStyleText from 'ol/style/Text.js';
import olStyleFill from 'ol/style/Fill.js';
import olStyleStroke from 'ol/style/Stroke.js';
import olSourceVector from 'ol/source/Vector.js';
import olEventsEventType from 'ol/events/EventType.js';
import {listen} from 'ol/events.js';
import olLayerVector from 'ol/layer/Vector.js';
import olFormatGeoJSON from 'ol/format/GeoJSON.js';

/** @suppress {extraRequire} */
import ngeoToolActivate from 'ngeo/misc/ToolActivate.js';


/**
 * @private
 */
export class Infra3dController {

  /**
   * @param {!angular.Scope} $scope Scope.
   * @param {!ngeo.FeatureOverlayMgr} ngeoFeatureOverlayMgr Ngeo FeatureOverlay
   *     manager.
   * @param {!ngeo.ToolActivateMgr} ngeoToolActivateMgr Ngeo ToolActivate manager
   *     service.
   * @private
   * @ngInject
   * @ngdoc controller
   * @ngname NgeoInfra3dController
   */
  constructor($scope, ngeoFeatureOverlayMgr, ngeoToolActivateMgr) {

    // Binding properties

    /**
     * @type {boolean}
     * @export
     */
    this.active;

    $scope.$watch(
      () => this.active,
      this.handleActiveChange_.bind(this)
    );

    /**
     * @type {!ol.Map}
     * @export
     */
    this.map;

    /**
     * URL of Infra3D client.
     * @type {string}
     * @export
     * @see https://www.infra3d.ch/api/apidoc/Reference/#init
     */
    this.infra3dClient;

    /**
     * Username used to login into Infra3D client.
     * @type {?string}
     * @export
     */
    this.username;

    /**
     * Password used to login into Infra3D client.
     * @type {?string}
     * @export
     */
    this.password;


    // Injected properties

    /**
     * @type {!angular.Scope}
     * @private
     */
    this.scope_ = $scope;

    /**
     * @type {!ngeo.ToolActivateMgr}
     * @private
     */
    this.ngeoToolActivateMgr_ = ngeoToolActivateMgr;


    // Inner properties

    /**
     * State on whether  plugin has been initialized already.
     * @type {boolean}
     * @private
     */
    this.infra3dInitialized_ = false;

    /**
     * The current location in the OpenLayers' map view projection.
     * @type {ol.Coordinate|null}
     * @private
     */
    this.location_ = null;

    /**
     * @type {!ngeo.map.FeatureOverlay} Feature overlay.
     * @private
     */
    this.featureOverlay_ = ngeoFeatureOverlayMgr.getFeatureOverlay();
    // console.assert(this.featureOverlay_);

    /**
     * Feature representing the current position.
     * @type {!ol.Feature}
     * @private
     */
    this.feature_ = new olFeature();

    /**
     * Geometry of the current position.
     * @type {ol.geom.Point}
     * @private
     */
    this.point_ = new olGeomPoint([0, 0]);

    this.feature_.setGeometry(this.point_);

    /**
     * @type {ol.style.Text}
     * @private
     */
    this.textStyle_ = new olStyleText({
      fill: new olStyleFill({color: '#DC2228'}),
      font: '900 30px "Font Awesome 5 Free"',
      rotation: -Math.PI / 4, // rotation in radians
      stroke: new olStyleStroke({color: '#ffffff', width: 3}),
      text: '\uf124'
    });

    /**
     * Style for the feature.
     * @type {ol.style.Style}
     * @private
     */
    this.featureStyle_ = new olStyleStyle({
      text: this.textStyle_
    });

    this.feature_.setStyle(this.featureStyle_);

    /**
     * @type {ol.EventsKey|null}
     * @private
     */
    this.handleMapClickListenerKey_ = null;

    /**
     * Available media.
     * @type {ol.source.Vector}
     * @private
     */
    this.ennSource_ = new olSourceVector();

    /**
     * @type {ol.layer.Vector}
     * @private
     */
    this.ennLayer_ = new olLayerVector({
      source: this.ennSource_,
      style: new olStyleStyle({
        stroke: new olStyleStroke({
          color: '#3399CC',
          width: 3.0
        })
      })
    });
  }

  /**
   * Called on initialization of the controller.
   */
  $onInit() {
    const infra3DToolActivate = new ngeoToolActivate(this, 'active');
    this.ngeoToolActivateMgr_.registerTool('mapTools', infra3DToolActivate);

    // === Watchers ===

    // (1) Watch for any change in the location
    this.scope_.$watch(
      () => this.location_,
      this.handleLocationChange_.bind(this)
    );

    // (2) Watch for both the active and location. When we have both, the
    //     state is considered 'ready'.
    this.scope_.$watch(
      () => {
        const isActive = this.active;
        const hasLocation = this.location_ !== null;
        return isActive && hasLocation;
      },
      this.handleReadyChange_.bind(this)
    );
  }

  /**
   * @private
   */
  initComponent_() {
    // Initialize location with current center of map.
    this.location_ = this.map.getView().getCenter() || [0, 0];

    // Infra3D iframe options
    const divId = 'infra3d';
    const infra3dOptions = {
      'easting': this.location_[0],
      'northing': this.location_[1],
      'epsg': 2056,
      'map': false,
      'layer': false,
      'navigation': false
    };

    // Automatically login if both username and password are present.
    if (this.username && this.password) {
      infra3dOptions['credentials'] = [this.username, this.password];
    }

    // Init Infra3D iframe
    window.infra3d.init(divId, this.infra3dClient, infra3dOptions, () => {
      // Register logout listener
      window.infra3d.setOnLogOut(this.handleLogout_, this);

      // Register listener when position changes wihtin infra3d
      window.infra3d.setOnPositionChanged(this.handleInfra3DOnPositionChanged, this);

      // Set infra3d state to initialized
      this.infra3dInitialized_ = true;

      // Get available media from infra3d as layer
      window.infra3d.getEnn(2056, function(enn) {
        const geojson = new olFormatGeoJSON();
        const features = geojson.readFeatures(enn);
        this.ennSource_.addFeatures(features);
        this.map.addLayer(this.ennLayer_);
      }, this, null);
    }, this);
  }

  /**
   * Called when the 'active' property of this component changes.
   * @param {boolean} active Active.
   * @private
   */
  handleActiveChange_(active) {
    if (active) {
      if (!this.infra3dInitialized_) {
        this.initComponent_();
      }
      this.handleMapClickListenerKey_ = listen(
        this.map,
        olEventsEventType.CLICK,
        this.handleMapClick_,
        this
      );
    } else {
      unByKey(this.handleMapClickListenerKey_);
      this.handleMapClickListenerKey_ = null;
    }
  }

  /**
   * Called when the 'location' property of this component changes.
   * @param {?ol.Coordinate} location Location, in OL map view projection.
   * @param {?ol.Coordinate} oldLocation The previous location.
   * @private
   */
  handleLocationChange_(location, oldLocation) {
    // Don't update if new location is empty
    if (location === null) {
      return;
    }

    // No need to do anything if the old value equals the new value
    if (oldLocation !== null && equals(location, oldLocation)) {
      return;
    }

    // Update point coordinates
    this.point_.setCoordinates(location);
  }

  /**
   * Called when the map is clicked while this component is active. Update the
   * location accordingly.
   * @param {ol.MapBrowserEvent} evt The map browser event being fired.
   * @private
   */
  handleMapClick_(evt) {
    if (this.infra3dInitialized_) {
      this.location_ = evt.coordinate;
      this.scope_.$apply();

      // send location to infra3d
      window.infra3d.loadAtPosition(this.location_[0], this.location_[1], {
        'epsg': 2056
      });
    }
  }

  /**
   * Called when the component 'virtual ready' state changes.
   *
   * When ready:
   *  - add the feature to the overlay
   *
   * When not ready:
   *  - remove the feature from the overlay
   *
   * @param {boolean} ready Whether the component is ready or not.
   * @param {boolean} oldReady Previous ready value.
   * @private
   */
  handleReadyChange_(ready, oldReady) {
    if (ready === oldReady) {
      return;
    }

    if (ready) {
      // Add current position to feature overlay
      this.featureOverlay_.addFeature(this.feature_);

      // Display available media
      this.ennLayer_.setVisible(true);

    } else {
      // Remove current position from feature overlay
      this.featureOverlay_.removeFeature(this.feature_);

      // Hide available media
      this.ennLayer_.setVisible(false);
    }
  }

  /**
   * Called when the user logs out of the infra3d plugin.
   * @private
   */
  handleLogout_() {
    // Register listener when position changes within infra3d
    window.infra3d.unsetOnPositionChanged();

    // Clear available media
    this.ennSource_.clear();
  }

  /**
   * Event called by the Infra3D-API when a position gets updated.
   *
   * Note: There are eleven parameters, but we only use the first two.
   * @param {number} easting Coordinate of the current position.
   * @param {number} northing Coordinate of the current position.
   * @param {number} height Coordinate of the current position.
   * @param {number} epsg Epsg-code for the coordinates of the current position.
   * @param {number} orientation Horizontal orientation of the camera (Azimuth).
   * @see https://www.infra3d.ch/api/apidoc/Reference/#onpositionchanged
   */
  handleInfra3DOnPositionChanged(easting, northing, height, epsg, orientation) {
    // Set icon location
    this.location_ = /** @type{ol.Coordinate} */ ([easting, northing]);

    // Set icon orientation
    let rotation = orientation - 45; // icon image offset
    rotation = ((rotation % 360) + 360) % 360; // modulo 360 to enforce positive result
    rotation *= Math.PI / 180; // convert to radians
    this.textStyle_.setRotation(rotation);

    this.scope_.$apply();
  }
}


const name = 'rudazInfra3d';
const module = angular.module(name, []).component(name, {
  bindings: {
    'active': '<',
    'map': '<',
    'infra3dClient': '<',
    'username': '<?',
    'password': '<?'
  },
  controller: Infra3dController,
  template: '<div id="infra3d"></div>'
});

export default  module;
